<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.reloop.reloopSubmission', 2), to: '/reports/reloop/submissions' }
    ]"
  >
    <template #right>
      <div class="flex items-center gap-4">
        <ButtonGroup v-if="authStore.details.userType === 1">
          <Button
            icon="file-csv"
            label="Export Answers"
            :is-loading="submissionsDownloading"
            outlined
            severity="secondary"
            @click="exportAnswers('byDateRange')"
          />
          <Dropdown position="bottom-left">
            <template #triggerContent>
              <Button
                icon="chevron-down"
                outlined
                severity="secondary"
              />
            </template>
            <DropdownItem @click="exportAnswers('allAnswers')">All</DropdownItem>
            <DropdownItem @click="exportAnswers('allAnswers')">Within date range</DropdownItem>
          </Dropdown>
        </ButtonGroup>
        <DatePicker
          v-model="dateRange"
          selection-mode="range"
          show-presets
          class="min-w-60"
          :presets="dateRangePresets"
          :months-visible="useBreakPoints({ xs: 1, sm: 2 })"
          @select="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      filter-display="menu"
      :global-filter-fields="['id', 'customerName', 'dealershipId', 'dealershipName']"
      data-key="id"
      :value="reloops"
      sort-field="createdAt"
      :sort-order="-1"
      removable-sort
      :paginator="true"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-reloop-submissions"
      state-storage="session"
      :loading="reloopsApiRequest.isLoading.value"
    >
      <template #empty>
        <Empty
          v-if="!reloopsApiRequest.isLoading.value"
          title="No data"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
      </template>

      <!-- <Column selection-mode="multiple" header-style="width: 3em"></Column> -->

      <!-- <Column field="id" header="#" :sortable="true">
        <template #body="{ data }">
          <a class="link" :href="'/agreement/' + data.agreementId + '/appointments/' + data.appointmentId">{{ data.id }}</a>
        </template>
      </Column> -->

      <Column
        field="customerName"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="'/agreement/' + data.agreementId + '/appointments/' + data.appointmentId"
            class="link"
          >
            {{ data.customerName }}
          </router-link>
        </template>
      </Column>

      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="dealershipsList"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="attended"
        header="Attended"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">{{ data.attended ? 'Yes' : 'No' }}</template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="max-w-[300px]"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="scores.totals.totalPercent"
        header="Score"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.scores.totals.totalPercent != null"
            :severity="getScoreColor(data.scores.totals.totalPercent)"
          >
            {{ data.scores.totals.totalPercent }}%
          </Chip>
        </template>
      </Column>

      <Column
        field="satisfaction"
        header="Satisfaction"
        :sortable="true"
      >
        <template #body="{ data }">
          <Chip
            v-if="data.satisfaction != null"
            :severity="getScoreColor(data.satisfaction * 10)"
          >
            {{ data.satisfaction }}
          </Chip>
        </template>
      </Column>

      <Column
        field="alertManagement"
        header="Alert"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Icon
            v-if="data.alertManagement"
            v-tippy="'Customer requested to alert management'"
            type="warning"
            class="text-warning"
          />
        </template>
        <template #filter="{ filterModel }">
          <Select
            v-model="filterModel.value"
            :options="[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' }
            ]"
            mode="multiple"
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="max-w-[300px]"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>

      <Column
        field="createdBy"
        header="Submitted By"
        :sortable="true"
        class="capitalize"
      />

      <Column
        field="createdAt"
        header="Submitted At"
        :sortable="true"
      >
        <template #body="{ data }">
          <span v-tippy="data.createdAt">{{ dayjs(data.createdAt).format('ll') }}</span>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Icon from '@/components/icon/Icon.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import Empty from '@/components/empty/Empty.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from 'primevue/api'
import Select from '@/components/forms/Select.vue'

import dayjs from 'dayjs'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import useApiRequest from '@/composables/useApiRequest'
import { useBreakPoints } from '@/composables/useWindowSize'
// utilities
import { alertToast } from '@/utilities/notification'
import { isEmptyObject } from '@/utilities'

import axios from 'axios'
import ButtonGroup from '@/components/button/ButtonGroup.vue'

const route = useRoute()
const router = useRouter()

const authStore = useAuthStore()

const dt = ref(null)

const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)

const dateRange = ref([dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')])
const dateRangePresets = [
  {
    label: 'This Week',
    value: {
      start: dayjs().startOf('week'),
      end: dayjs().endOf('week')
    }
  },
  {
    label: 'This Month',
    value: {
      start: dayjs().startOf('month'),
      end: dayjs().endOf('month')
    }
  },
  {
    label: 'Last Month',
    value: {
      start: dayjs().subtract(1, 'month').startOf('month'),
      end: dayjs().subtract(1, 'month').endOf('month')
    }
  }
]

const reloopsApiRequest = useApiRequest()

const reloops = ref([])

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN },
    attended: { value: null, matchMode: FilterMatchMode.EQUALS },
    alertManagement: { value: null, matchMode: FilterMatchMode.EQUALS }
  }
}
initFilters()

function clearFilters() {
  // remove from session storage
  sessionStorage.removeItem('dt-reloop-submissions')
  initFilters()
}

function setRouteFilters() {
  if (route.query && !isEmptyObject(route.query)) {
    clearFilters()
    for (const [key, value] of Object.entries(route.query)) {
      if (key === 'dealerships') {
        // convert value (csl) string to array of numbers
        filters.value.dealershipId.value = value.split(',').map(Number)
      }
      if (key === 'alertManagement') {
        filters.value.alertManagement.value = value === 'true'
      }
      if (key === 'attended') {
        filters.value.attended.value = value === 'true'
      }
      if (key === 'dateRange') {
        dateRange.value = value.split(',').map(d => new Date(d))
      }
    }
  }
  // Remove url query params  using router replace
  router.replace({ query: {} })
}

setRouteFilters()

function fetchReloops() {
  if (!dateRange.value[0] || !dateRange.value[1]) {
    return
  }
  try {
    reloopsApiRequest
      .send({
        endpoint: '/v1/reloop',
        params: {
          startDate: dayjs(dateRange.value[0]).format('YYYY-MM-DD'),
          endDate: dayjs(dateRange.value[1]).format('YYYY-MM-DD'),
          dealerships: dealershipsAllowed.value.map(d => d.id).join(',')
        }
      })
      .then(response => {
        if (response.success) {
          reloops.value = response.data
        } else {
          alertToast('Failed to fetch ReLoops', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch ReLoops', err.message, 'error')
  }
}
fetchReloops()

const dealershipsList = computed(() => {
  return dealershipsAllowed.value.map(d => {
    return {
      value: d.id,
      label: d.name
    }
  })
})

function getScoreColor(score) {
  if (score <= 70) {
    return 'danger'
  } else if (score <= 80) {
    return 'warning'
  } else {
    return 'success'
  }
}

function onDateRangeChange() {
  // Refetch reloops
  fetchReloops()
}

const submissionsDownloading = ref(null)

async function exportAnswers(range) {
  try {
    // TODO: Convert to UseApiRequest
    submissionsDownloading.value = true

    let url = ''
    if (range == 'byDateRange') {
      url =
        import.meta.env.VITE_API_BASE_URL +
        '/v1/reloop/export?start=' +
        dayjs(dateRange.value[0]).format('YYYY-MM-DD') +
        '&end=' +
        dayjs(dateRange.value[1]).format('YYYY-MM-DD')
    } else if (range == 'allAnswers') {
      url = import.meta.env.VITE_API_BASE_URL + '/v1/reloop/export'
    }

    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
    const data = await axios(url, { withCredentials: true, headers, responseType: 'blob' })

    if (data.status != 200) {
      if (data.http.status === 401) {
        throw Error('401 Unauthorized')
      } else {
        throw Error('Error fetching data')
      }
    }

    // If everything is fine
    const fileURL = window.URL.createObjectURL(new Blob([data.data]))
    const fURL = document.createElement('a')

    fURL.href = fileURL
    fURL.setAttribute('download', 'reloop_submissions.csv')
    // fURL.setAttribute('target', '_blank');
    document.body.appendChild(fURL)

    fURL.click()
  } catch (err) {
    console.error(err)
    alertToast('Failed to export', err.message, 'error')
  }
  submissionsDownloading.value = false
}
</script>

<style></style>
